import { useEffect, useRef } from 'react';

import { DownOutlined } from '@ant-design/icons';
import { Button, Flex } from '@revenuemarket/cds';
import { Colors, typographyStyle } from '@revenuemarket/design';
import { Typography } from 'antd';

import { GroupTreeItem } from '~apis/custom-label';
import { FlattenTreeResult, flattenTree } from '~components/ui/HorizontalTreeTable/Table';

import GroupTreeSelect from './GroupTreeSelect';
import MigrationListItem from './common/component/MigrationListItem';
import useGroupLabelTreeStore from './store/groupLabelTreeStore';

interface Props {
  targetItems: FlattenTreeResult<GroupTreeItem>[];
  depth: number;
  changeMap: Record<string, string>;
  setChangeMap: (value: Record<string, string>) => void;
  parentItem: FlattenTreeResult<GroupTreeItem>;
}

const GroupMigrationList: React.FC<Props> = ({
  targetItems,
  depth,
  changeMap,
  setChangeMap,
  parentItem,
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const targetItemKeys = targetItems.map((item) => item.key);
  const { groupLabelTree, setGroupLabelTree } = useGroupLabelTreeStore();
  const flattenedTree = flattenTree(groupLabelTree);
  const flattenedTreeOptions = flattenedTree.filter(
    (item) => !!item.value?.name && !targetItemKeys.includes(item.key), // 자신과 자신 하위계정은 제외
  );

  useEffect(() => {
    if (targetItems?.length > 0 && Object.values(changeMap).length === 0 && parentItem) {
      const initialMap: Record<string, string> = {};

      targetItems.forEach((item) => {
        initialMap[item.value?.id as unknown as string] = '';
      });
      setChangeMap(initialMap);
    }
  }, [targetItems, changeMap, parentItem, setChangeMap]);

  return (
    <Flex direction="column" gap={12}>
      <Flex
        direction="column"
        style={{
          marginTop: 17,
        }}
      >
        <Typography.Text
          style={{
            ...typographyStyle['14_400'],
          }}
        >
          거래 내역과 자동화 규칙에 적용된 데이터를 이관할 항목을 선택해주세요.
        </Typography.Text>
      </Flex>
      <Flex direction="column" gap={8}>
        {targetItems.map((child) => {
          const target = flattenedTree.find((node) => node.key.toString() === child.key);

          const breadcrumbItems = target?.namePath
            .split('.')
            .slice(depth - 1, target.namePath.split('.').length)

            .map((key) => {
              return {
                key: key,
                title: key,
              };
            });

          const defaultActiveKey = flattenedTree.find(
            (item) => item.value?.id === changeMap[child.value?.id as number],
          )?.key;

          return (
            <MigrationListItem
              breadcrumbItems={breadcrumbItems}
              key={child.key}
              treeSelectSection={
                <GroupTreeSelect
                  showReset
                  treeRef={wrapperRef}
                  closeOnSelect
                  footerElement={<div />}
                  defaultActiveKey={defaultActiveKey ? [defaultActiveKey] : []}
                  style={{ flex: 1 }}
                  treeList={flattenedTreeOptions}
                  onSelect={(id) => {
                    if (!child.value) return;

                    setChangeMap({
                      ...changeMap,
                      [child.value.id as number]: id,
                    });
                  }}
                  triggerElement={
                    <Button border>
                      <Flex
                        justify="space-between"
                        align="center"
                        style={{
                          width: '100%',
                        }}
                      >
                        <Typography.Text
                          style={{
                            color: Colors.gray800,
                            ...typographyStyle['14_400'],
                          }}
                        >
                          {changeMap[child.value?.id as number]
                            ? flattenedTree.find(
                                (item) =>
                                  item.value?.id.toString() ===
                                    changeMap[child.value?.id as number] && !!item.value.name,
                              )?.value?.name ?? '빈 값'
                            : '빈 값'}
                        </Typography.Text>
                        <DownOutlined
                          style={{
                            color: Colors.gray700,
                          }}
                        />
                      </Flex>
                    </Button>
                  }
                />
              }
            />
          );
        })}
      </Flex>
    </Flex>
  );
};

export default GroupMigrationList;
